import { CircularProgress, Link, Stack, Typography, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

import BasicModal from '@/components/common/BasicModal';
import usePostCancelConsultation from '@/hooks/rest/usePostCancelConsultation';
import { PrescriptionAssistantModal } from '@/shared-ui/PrescriptionAssistantModal/PrescriptionAssistantModal';

export type ConsultationDirectCancelPopupButtonProps = {
  consultationId: string | number;
  onClose?: () => void;
  isButton?: boolean;
};

const StyledBasicModal = styled(BasicModal)(({ theme: muiTheme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: muiTheme.palette.background.paper,
  borderRadius: '4px',
  width: '490px',
  outline: 'none',
  boxShadow:
    '0px 9px 46px 8px rgba(0, 0, 0, 0.12), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 11px 15px -7px rgba(0, 0, 0, 0.20)',
}));

const ConsultationDirectCancelPopupButton = ({
  consultationId,
  onClose,
  isButton,
  ...rest
}: ConsultationDirectCancelPopupButtonProps) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);

  const handleOpen = () => setModalOpen(true);

  const {
    mutate: cancelConsultation,
    loading: isLoading,
    data: result,
    error: hasError,
  } = usePostCancelConsultation({
    consultationId,
  });
  const didError = (isLoading == false && hasError) || result === false;
  const didSucceed = isLoading == false && result === true && !didError;

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setModalOpen(false);
  };

  const PopupButton = (
    <Button
      color={'secondary'}
      onClick={handleOpen}
      sx={{ textDecoration: 'none', cursor: 'pointer' }}
      data-testid="cancel-direct-popup-trigger"
      {...rest}
    >
      Cancel
    </Button>
  );

  const PopupLink = (
    <Link
      onClick={handleOpen}
      sx={{ textDecoration: 'none', cursor: 'pointer' }}
      data-testid="cancel-direct-popup-trigger"
      {...rest}
    >
      Cancel
    </Link>
  );

  const PopupTrigger = isButton ? PopupButton : PopupLink;

  const ErrorContent = (
    <PrescriptionAssistantModal
      open={modalOpen}
      title="Cancel Pending Consultation"
      severity="error"
      body={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="body1" color={theme.palette.text.primary}>
            There was an error cancelling this consultation. Please try again later.
          </Typography>
        </Stack>
      }
      confirmLabel="Close"
      onConfirm={handleClose}
    />
  );

  const LoadingContent = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      height={206}
      data-testid="consultation-direct-cancel-popup-loading-content"
    >
      <CircularProgress color="inherit" />
    </Stack>
  );

  const SuccessContent = (
    <PrescriptionAssistantModal
      open={modalOpen}
      title="Cancellation Successful"
      severity="success"
      body={
        <Typography variant="body1" color={theme.palette.text.primary}>
          This consultation has been cancelled.
        </Typography>
      }
      confirmLabel="Close"
      onConfirm={handleClose}
    />
  );

  const SubmissionContent = (
    <PrescriptionAssistantModal
      open={modalOpen}
      title="Cancel Pending Consultation"
      severity="warning"
      body={
        <Typography variant="body1" color={theme.palette.text.primary}>
          This action will not send any communication to the patient.
        </Typography>
      }
      confirmLabel="Yes, Cancel Consultation"
      closeLabel="No, Keep Consultation"
      isLoading={isLoading}
      onConfirm={() => cancelConsultation({ cancellationReason: '' })}
      onClose={handleClose}
    />
  );

  return consultationId ? (
    <>
      {PopupTrigger}

      <StyledBasicModal open={modalOpen} handleClose={handleClose} data-testid="consultation-direct-cancel-popup-modal">
        {/* When loading show loader */}
        {isLoading && !didError && LoadingContent}

        {/* We're not loading and have an error */}
        {!isLoading && didError && ErrorContent}

        {/* We're not loading and have no result -- Ready to send Req*/}
        {!isLoading && !didSucceed && !didError && SubmissionContent}

        {/* We're not loading and we do have a result  */}
        {!isLoading && didSucceed && !didError && SuccessContent}
      </StyledBasicModal>
    </>
  ) : null;
};

export default ConsultationDirectCancelPopupButton;
